import { SagaIterator } from 'redux-saga';
import { IPayoutAccount, IUpdateTaxAction, SetUserPayload } from '../../types/user';
import { ICreatePayoutAccountParams, ISagaAction, IUpdatePayoutAccountParams } from '../../types';
import { call, put, race, select, take } from 'redux-saga/effects';
import { Api, getTranslationKey } from '../../utils';
import ApiErrors from '../../utils/API/APIErrors';
import {
  errorsGlobalError,
  fetchPayoutAccountSuccessAction,
  fetchShopPayinPaymentMethod,
  getAvailableCurrenciesSuccessAction,
  setRequiredTaxSavedAction,
  showNotifyFail,
  showNotifySuccess,
  updateBillingAddress
} from '../../actions';
import { getUser } from '../../selectors';
import { omit } from 'lodash';
import { Dispatch } from 'redux';
import { getPaymentInfo } from '../../selectors/paymentInfo';

interface ApiError extends Error {
  code?: number;
  response?: {
    data?: any;
  };
}

const getTaxDataForAPI = (data: IUpdateTaxAction, taxConfirmed: boolean) => {
  const { vatId, taxId, registrationNumber, commercialRegister, smallBusinessOwner, tax, country } =
    data.form;
  const dataForApi = !data.isEdit
    ? {
        currency: data.form.currency,
        taxSettings: {
          smallBusinessOwner,
          endCustomerPercent: tax
        },
        registrationProcess: {
          setTaxDetails: true
        },
        countryId: country
      }
    : {
        currency: data.form.currency,
        taxSettings: {
          smallBusinessOwner,
          endCustomerPercent: tax,
          taxId,
          registrationNumber,
          commercialRegister,
          vatId
        }
      };

  if (taxConfirmed) {
    delete dataForApi.taxSettings.smallBusinessOwner;
    delete dataForApi.taxSettings.endCustomerPercent;
  }

  return dataForApi;
};

const KNOWN_ERROR_TRANSLATION_KEYS: Record<string, string> = {
  CreditBalanceIsNotZero: 'settingsSection.CreditBalanceIsNotZero',
  VatIdIsNotValid: 'settingsSection.vatValidationError'
};

const getResponseErrorMessage = (errorObj?: {
  name?: string;
  message?: string;
  details?: { message?: string }[];
}): string => {
  if (!errorObj) return '';

  if (errorObj.name && KNOWN_ERROR_TRANSLATION_KEYS[errorObj.name]) {
    return getTranslationKey(KNOWN_ERROR_TRANSLATION_KEYS[errorObj.name]);
  }

  return errorObj.details?.[0]?.message || errorObj.message || '';
};

export const updateUserTaxSaga = function* ({
  payload
}: ISagaAction<IUpdateTaxAction>): SagaIterator {
  let user;
  let errorMessage = '';
  try {
    if (!navigator.onLine) {
      yield put(showNotifyFail({ message: getTranslationKey('offlineMessage') }));

      return;
    }

    user = yield select(getUser);
    const { address: paymentInfoAddress } = yield select(getPaymentInfo);

    // Wait for billing address update to complete before updating tax settings
    if (payload.waitForBillingAddressUpdate) {
      const { failed } = yield race({
        completed: take('BILLING_ADDRESS_UPDATE_COMPLETED'),
        failed: take('BILLING_ADDRESS_UPDATE_FAILED')
      });

      if (failed) {
        yield put(showNotifyFail());

        return;
      }
    }

    yield put({ type: 'SET_USER', payload: { ...user, isUpdating: true } });

    const data = getTaxDataForAPI(payload, user.taxSettings.isConfirmed);
    const response = yield call(Api.User.updateUserByID, data, user._id);

    if (response.code === 0) {
      yield put(showNotifyFail({ message: getTranslationKey('offlineMessage') }));
      yield put({ type: 'SET_USER', payload: { ...user, isUpdating: false } });

      return;
    }

    const errorObj = response.response?.data?.error;
    errorMessage = getResponseErrorMessage(errorObj);

    if (errorMessage) {
      yield put(showNotifyFail({ message: errorMessage }));
      yield put({
        type: 'SET_USER_TAX_SETTINGS',
        payload: {
          ...user.taxSettings,
          errorMessage,
          vatIdIsValid: false
        }
      });
    }

    ApiErrors.checkOnApiError(response);

    // Only update billing address if called from tax modal
    // The payment page already calls updateBillingAddress directly in BillingAddressForm
    if (payload.isTaxModal && data.countryId && paymentInfoAddress) {
      yield put(
        updateBillingAddress({
          email: paymentInfoAddress.email,
          firstName: paymentInfoAddress.firstName,
          lastName: paymentInfoAddress.lastName,
          company: paymentInfoAddress.company,
          street: paymentInfoAddress.street,
          streetNumber: paymentInfoAddress.streetNumber,
          zipcode: paymentInfoAddress.zipcode,
          city: paymentInfoAddress.city,
          country: data.countryId,
          routerPath: '',
          isTaxSettingsModal: true,
          justUpdate: true
        })
      );

      // Wait for billing address update result before closing the modal
      const { failed } = yield race({
        completed: take('BILLING_ADDRESS_UPDATE_COMPLETED'),
        failed: take('BILLING_ADDRESS_UPDATE_FAILED')
      });

      if (failed) {
        localStorage.setItem('taxModalBillingAddressFailed', 'true');
        yield put(showNotifyFail());

        return;
      }
    }

    localStorage.removeItem('taxModalBillingAddressFailed');

    if (!payload.isEdit) {
      yield put({
        type: 'set_registration_process',
        payload: { key: 'setTaxDetails', value: true }
      });
    }

    const setUsrPayload: SetUserPayload = {};
    if (data.currency) {
      setUsrPayload.currency = data.currency;
    }

    if (data.countryId) {
      setUsrPayload._country = data.countryId;
    }

    yield put({
      type: 'SET_USER',
      payload: {
        ...setUsrPayload,
        isUpdating: false,
        taxSettings: {
          ...response.result.taxSettings,
          isConfirmed: response.result.taxSettings.isConfirmed,
          errorMessage,
          vatIdIsValid: payload.isPaymentPage ? !errorMessage : false
        }
      }
    });

    yield put({
      type: 'SET_USER_TAX_SETTINGS',
      payload: {
        ...response.result.taxSettings,
        isConfirmed: response.result.taxSettings.isConfirmed,
        errorMessage,
        vatIdIsValid: payload.isPaymentPage ? !errorMessage : false
      }
    });

    yield put(setRequiredTaxSavedAction(true));
    yield put(showNotifySuccess({}));

    if (data.currency !== user.currency) {
      yield put(fetchShopPayinPaymentMethod());
    }

    if (payload.closeForm) {
      payload.closeForm();
    }
  } catch (e) {
    if (!errorMessage) {
      yield put(showNotifyFail());
    }

    yield put({
      type: 'SET_USER',
      payload: {
        ...user,
        isUpdating: false
      }
    });

    yield put({
      type: 'SET_USER_TAX_SETTINGS',
      payload: {
        ...user.taxSettings,
        vatIdIsValid: false
      }
    });
  }
};

export const updateSequentialUploadsSaga = function* ({
  payload
}: ISagaAction<boolean>): SagaIterator {
  const user = yield select(getUser);
  const previous = Boolean(user?.sequentialUploads);

  // Optimistic flip: the upload saga reads getSequentialUploads when it
  // spins up forks, so the new value must land in redux before the BE round-trip.
  yield put({ type: 'SET_SEQUENTIAL_UPLOADS', payload });

  try {
    if (!navigator.onLine) {
      throw new ApiErrors(getTranslationKey('offlineMessage'), 0);
    }

    const response = yield call(Api.User.updateUserByID, { sequentialUploads: payload }, user._id);

    // A network error resolves with code 0 instead of rejecting, and
    // checkOnApiError treats 0 as falsy — surface it as an offline failure
    // so the catch below notifies the user and rolls back the optimistic flip.
    if (response?.code === 0) {
      throw new ApiErrors(getTranslationKey('offlineMessage'), 0);
    }

    ApiErrors.checkOnApiError(response);
    // Surface the "reload to apply" hint in the success toast: an in-flight
    // upload (or another tab) won't pick up the new concurrency until reload.
    yield put(
      showNotifySuccess({
        message: getTranslationKey('uploadEndpoint.disableConcurrentReloadHint')
      })
    );
  } catch (e) {
    const message = (e as ApiErrors)?.message;

    // Roll back to the previous value when the change can't be persisted.
    yield put({ type: 'SET_SEQUENTIAL_UPLOADS', payload: previous });
    yield put(showNotifyFail(message ? { message } : undefined));
  }
};

export const setNewDesignSaga = function* ({ payload }: ISagaAction<boolean>): SagaIterator {
  const user = yield select(getUser);

  try {
    const response = yield call(Api.User.updateUserByID, { newDesign: payload }, user._id);

    // A network error resolves with code 0, which checkOnApiError lets through.
    if (response?.code === 0) {
      throw new ApiErrors(getTranslationKey('offlineMessage'), 0);
    }

    ApiErrors.checkOnApiError(response);
    // Templates, collections and the open editor are all loaded for the current
    // design, so reload instead of flipping the flag in place.
    yield call([window.location, window.location.reload]);
  } catch (e) {
    const message = (e as ApiErrors)?.message;

    yield put(showNotifyFail(message ? { message } : undefined));
  }
};

export const fetchPayoutAccountSaga = function* (): SagaIterator {
  try {
    const response = yield call(Api.User.fetchPayoutAccount);
    ApiErrors.checkOnApiError(response);
    yield put(fetchPayoutAccountSuccessAction(response.result));
  } catch (e) {
    const error = e as ApiError;
    if (error.code === 404) {
      yield put(fetchPayoutAccountSuccessAction(null));

      return;
    }
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

export const getAvailableCurrenciesSaga = function* ({
  payload
}: ISagaAction<string | undefined>): SagaIterator {
  try {
    const response = yield call(Api.User.getAvailableCurrencies, payload);
    ApiErrors.checkOnApiError(response);
    yield put(getAvailableCurrenciesSuccessAction(response.result.currencies));
  } catch (e) {
    const error = e as ApiError;
    if (error.code === 404) return;
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

export const createPayoutAccountSaga = function* ({
  payload
}: ISagaAction<ICreatePayoutAccountParams>): SagaIterator {
  try {
    const response = yield call(Api.User.createPayoutAccount, payload);
    ApiErrors.checkOnApiError(response);
    yield put(fetchPayoutAccountSuccessAction(response.result));
    yield put(showNotifySuccess({}));
  } catch (e) {
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

export async function createPayoutAccount(
  params: ICreatePayoutAccountParams,
  dispatch: Dispatch
): Promise<{ result: IPayoutAccount | null; error: any | null }> {
  let response;
  try {
    response = await Api.User.createPayoutAccount(params);
    ApiErrors.checkOnApiError(response);
    dispatch(fetchPayoutAccountSuccessAction(response.result));
    dispatch(showNotifySuccess({}));

    return response;
  } catch (e) {
    dispatch(errorsGlobalError(e as ErrorEvent));

    return response.response.data;
  }
}

export const updatePayoutAccountSaga = function* ({
  payload
}: ISagaAction<IUpdatePayoutAccountParams>): SagaIterator {
  try {
    const response = yield call(Api.User.updatePayoutAccount, omit(payload, 'id'), payload.id);
    ApiErrors.checkOnApiError(response);
    yield put(fetchPayoutAccountSuccessAction(response.result));
    yield put(showNotifySuccess({}));
  } catch (e) {
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

export async function updatePayoutAccount(
  params: IUpdatePayoutAccountParams,
  dispatch: Dispatch
): Promise<{ result: IPayoutAccount | null; error: any | null }> {
  let response;
  try {
    response = await Api.User.updatePayoutAccount(omit(params, 'id'), params.id);
    ApiErrors.checkOnApiError(response);
    dispatch(fetchPayoutAccountSuccessAction(response.result));
    dispatch(showNotifySuccess({}));

    return response;
  } catch (e) {
    dispatch(errorsGlobalError(e as ErrorEvent));

    return response.response.data;
  }
}
